import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';

export default function Blog() {
  const blogPosts = [
    {
      slug: 'repair-vs-replace-furnace',
      title: 'When to Repair vs Replace Your Furnace',
      excerpt: 'Making the right decision between repairing and replacing your furnace can save you thousands of dollars. Learn the key factors to consider.',
      date: '2026-01-15',
      readTime: '5 min read',
      category: 'Furnaces',
      image: 'https://images.unsplash.com/photo-1605281317010-fe5ffe798166?w=1200&q=80&auto=format&fit=crop',
      imageAlt: 'Aging residential furnace in a basement utility room'
    },
    {
      slug: 'heat-pump-rebates-ontario-2026',
      title: 'Heat Pump Rebates in Ontario 2026',
      excerpt: 'Everything you need to know about federal and provincial rebates for heat pump installations this year. Up to $6,500 in savings available.',
      date: '2026-01-08',
      readTime: '7 min read',
      category: 'Heat Pumps',
      image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1200&q=80&auto=format&fit=crop',
      imageAlt: 'Outdoor heat pump unit on a concrete pad next to a house'
    },
    {
      slug: 'ac-freezing-up',
      title: 'Why Your AC is Freezing Up',
      excerpt: 'Ice on your air conditioner in summer? It\'s more common than you think. Here are the causes and solutions to this frustrating problem.',
      date: '2025-12-20',
      readTime: '4 min read',
      category: 'Air Conditioning',
      image: 'https://images.unsplash.com/photo-1632170875878-cdc6e8c2ed90?w=1200&q=80&auto=format&fit=crop',
      imageAlt: 'Outdoor central air conditioning condenser unit'
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-[var(--color-slate-blue)] text-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">HVAC Tips & Resources</h1>
          <p className="text-xl text-gray-100 max-w-3xl">
            Expert advice, maintenance tips, and industry updates to help you make informed decisions about your home's heating and cooling systems.
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 max-w-4xl mx-auto">
            {blogPosts.map((post) => (
              <article key={post.slug} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                <img
                  src={post.image}
                  alt={post.imageAlt}
                  width={1200}
                  height={600}
                  loading="lazy"
                  className="w-full object-cover aspect-[2/1]"
                />
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                    <span className="bg-[var(--color-warm-amber)] text-white px-3 py-1 rounded text-xs font-semibold">
                      {post.category}
                    </span>
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold mb-3 text-[var(--color-text-dark)]">
                    <Link to={`/blog/${post.slug}`} className="hover:text-[var(--color-slate-blue)]">
                      {post.title}
                    </Link>
                  </h2>
                  <p className="text-gray-600 mb-4">
                    {post.excerpt}
                  </p>
                  <Link 
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center gap-2 text-[var(--color-slate-blue)] font-semibold hover:text-[var(--color-warm-amber)]"
                  >
                    Read More
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {/* Newsletter CTA */}
          <div className="mt-16 bg-[var(--color-neutral-bg)] rounded-lg p-8 max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4 text-[var(--color-text-dark)]">
              Stay Informed About Your HVAC System
            </h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Get seasonal maintenance reminders, energy-saving tips, and exclusive service offers delivered to your inbox.
            </p>
            <form
              className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
              onSubmit={(e) => {
                e.preventDefault();
                console.log('Newsletter signup (demo only)');
              }}
            >
              <label htmlFor="newsletter-email" className="sr-only">
                Email address
              </label>
              <input
                id="newsletter-email"
                type="email"
                name="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--color-slate-blue)] focus:border-transparent"
                required
              />
              <button
                type="submit"
                className="bg-[var(--color-slate-blue)] text-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors"
              >
                Subscribe
              </button>
            </form>
            <p className="text-xs text-gray-500 mt-4 italic">
              Note: This is a demo form on a portfolio site. No actual subscription will be created.
            </p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 bg-[var(--color-neutral-bg)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-center text-[var(--color-text-dark)]">
            Browse by Category
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {['Furnaces', 'Air Conditioning', 'Heat Pumps', 'Maintenance', 'Indoor Air Quality', 'Energy Efficiency', 'Troubleshooting', 'Industry News'].map((category) => (
              <button
                key={category}
                className="bg-white rounded-lg px-4 py-3 text-center hover:shadow-md transition-shadow text-gray-700 hover:text-[var(--color-slate-blue)] font-medium"
                onClick={() => console.log(`Filter by ${category} (demo)`)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[var(--color-slate-blue)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 text-white">
            Need Professional HVAC Service?
          </h2>
          <p className="text-xl mb-8 text-gray-100 max-w-2xl mx-auto">
            Don't DIY your HVAC problems. Get expert help from licensed technicians.
          </p>
          <Link 
            to="/contact" 
            className="inline-flex items-center gap-2 bg-[var(--color-warm-amber)] text-white px-8 py-4 rounded text-lg font-semibold hover:bg-opacity-90 transition-colors"
          >
            Schedule Service Call
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}